// =============================================================================
// InvitesAdmin.jsx — Manage → Invitations: the go-live invite utility (P4c)
// =============================================================================
// Sends the onboarding invitation to caseworkers who have NEVER been invited —
// primarily the accounts the Contacts import created silently (John's model,
// 5 July 2026: create silently now, invite systematically at go-live).
//
// Who counts as "never invited": role=shelter, active, profiles.invited_at
// IS NULL (db-44). Each successful send stamps invited_at, so the list shrinks
// as you go, re-runs never double-invite, and a stopped batch resumes cleanly.
//
// THE WALL (docs/staff-caseworker-separation-plan.md): a caseworker's NAME and
// EMAIL live on their user_shelters MEMBERSHIP contact — NOT on profiles.staff_*
// (which is Zephyr staff only, and was nulled for caseworkers by db-141). So the
// list + the invite email address both read from the membership contact, keyed by
// user_id. (Reading staff_user_email here is what left the Email column blank.)
//
// The email itself is the SAME caseworker onboarding email new manual accounts
// get (portal_config template + fresh magic link), sent by the admin-users
// Edge Function's `send_invite` action via SMTP2GO directly — it does NOT go
// through the send-email function, so the pre-go-live test-BCC there does not
// intercept these. (Still remove that BCC before go-live for everything else.)
//
// ⚠️ Requires `supabase functions deploy admin-users` (the send_invite action
// does not ship with the Vercel push).

import React from "react";
import { useSession } from "../../app/SessionContext.jsx";
import { loadCustomerTypeCategories } from "../../data/lookups";
import { getConfigValue } from "../../data/portalConfig";
import { listShelterProfiles, updateProfileById } from "../../data/profiles";
import { listResourceDocuments } from "../../data/resourceDocuments";
import { listAllShelters, updateShelterById } from "../../data/shelters";
import { listAllUserShelters } from "../../data/userShelters";
import { usePageSize } from "../../hooks/usePageSize";
import { confirmDialog } from "../../services/dialogService";
import { logAudit } from "../../utils/auditLog";
import { GLYPH } from "../../utils/glyphs";
import { logger } from "../../utils/logger";
import { getSupabaseClient } from "../../utils/supabase";
import { Btn, Card } from "../../utils/ui.jsx";
import { PageNav } from "../common/PageNav.jsx";

const EDGE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-users`;

export function InvitesAdmin() {
  const { user } = useSession();
  const [loading, setLoading] = React.useState(true);
  const [profiles, setProfiles] = React.useState([]);
  const [memberships, setMemberships] = React.useState([]);
  const [shelters, setShelters] = React.useState([]);
  const [catByType, setCatByType] = React.useState(() => new Map());
  const [categories, setCategories] = React.useState([]);
  const [stateFilter, setStateFilter] = React.useState("");
  // Filter by the broad DFV Service CATEGORY (option_category: DFV Refuge /
  // Intensive Support Org. / First Nation / Other), matching the Audience /
  // Feedback / Telemarketing screens — not by an individual service.
  const [categoryFilter, setCategoryFilter] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [progress, setProgress] = React.useState({ done: 0, total: 0 });
  const [failures, setFailures] = React.useState([]);
  const [lastRun, setLastRun] = React.useState(null);
  // Step 1 (go-live): reveal + reactivate the parked (deactivated) DFV Services.
  const [showInactive, setShowInactive] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [activating, setActivating] = React.useState(false);
  // Step 2: which caseworkers to actually invite (default = all in the filtered
  // set; the admin can de-select individuals before sending).
  const [selectedUserIds, setSelectedUserIds] = React.useState([]);
  // Step-2 list sorting (Name / Email / DFV Service(s)) + pagination. Sorting is
  // DISPLAY-ONLY — it never touches the `targets` set the selection is keyed on, so
  // changing the sort never resets the admin's de-selections.
  const [pageSize] = usePageSize();
  const [sortCol, setSortCol] = React.useState("name");
  const [sortDir, setSortDir] = React.useState("asc");
  const [page, setPage] = React.useState(0);
  // Resources attachment — now CENTRAL: it's configured on the welcome/invitation
  // TEMPLATE (Manage → System Content → Automatic Emails), so the SAME file attaches
  // to both the invitation and the account-creation email, and the Edge Function
  // reads it from the template. This screen only DISPLAYS what will attach (read
  // only) so the admin isn't setting it in two places
  // (docs/welcome-email-rich-plan.md §0 Q2).
  const [resourceDocs, setResourceDocs] = React.useState([]);
  const [templateAttachment, setTemplateAttachment] = React.useState({
    enabled: true,
    document_id: "",
  });

  const load = React.useCallback(function () {
    setLoading(true);
    Promise.all([
      listShelterProfiles(),
      listAllUserShelters(),
      listAllShelters(),
      loadCustomerTypeCategories(),
      listResourceDocuments(),
      getConfigValue("onboarding_caseworker_template"),
    ]).then(function (results) {
      setProfiles(results[0] || []);
      setMemberships(results[1] || []);
      setShelters(results[2] || []);
      setCatByType(results[3]?.byType || new Map());
      setCategories(results[3]?.categories || []);
      setResourceDocs(results[4] || []);
      const att = results[5]?.attachment || {};
      setTemplateAttachment({ enabled: att.enabled !== false, document_id: att.document_id || "" });
      setLoading(false);
    });
  }, []);
  React.useEffect(load, [load]);

  // What the template config says will attach (read-only mirror). The EF applies it
  // server-side; this screen doesn't pass an attachment id.
  const activeAttachmentId =
    templateAttachment.enabled && templateAttachment.document_id
      ? templateAttachment.document_id
      : "";
  const attachmentDoc = resourceDocs.find(function (d) {
    return d.id === templateAttachment.document_id;
  });

  // Lookup maps.
  const shelterByCode = {};
  shelters.forEach(function (s) {
    shelterByCode[s.shelter_code] = s;
  });
  // Broad category for a shelter (via its customer_type → option_category).
  const categoryOfCode = function (code) {
    const sc = shelterByCode[code];
    return sc ? catByType.get(sc.customer_type) || "" : "";
  };
  const codesByUser = {};
  memberships.forEach(function (m) {
    if (!m.user_id) return;
    if (!codesByUser[m.user_id]) codesByUser[m.user_id] = [];
    codesByUser[m.user_id].push(m.shelter_code);
  });
  // THE WALL: the contact (name/email) comes from the membership, keyed by user_id.
  // Person-level fields fan out across a person's memberships, so any row is
  // correct — prefer one that actually carries an email.
  const contactByUser = {};
  memberships.forEach(function (m) {
    if (!m.user_id) return;
    const cur = contactByUser[m.user_id];
    if (!cur || (!cur.email && m.email)) contactByUser[m.user_id] = m;
  });
  const contactName = function (p) {
    const c = contactByUser[p.id];
    const nm = c ? `${c.first_name || ""} ${c.last_name || ""}`.trim() : "";
    return p.display_name || nm || (c?.email ?? "") || "(no name)";
  };
  const contactEmail = function (p) {
    return contactByUser[p.id]?.email || "";
  };

  // The never-invited set (db-44 semantics), enriched with shelter codes + the
  // membership contact's name/email.
  const neverInvited = profiles
    .filter(function (p) {
      return !p.deactivated && !p.invited_at;
    })
    .map(function (p) {
      return {
        ...p,
        shelterCodes: codesByUser[p.id] || [],
        name: contactName(p),
        email: contactEmail(p),
      };
    });
  const alreadyInvited = profiles.filter(function (p) {
    return !p.deactivated && p.invited_at;
  }).length;

  // A user's ACTIVE service codes (an inactive service isn't invite-eligible yet —
  // the two-step go-live: activate first, then invite).
  const activeCodesOf = function (p) {
    return p.shelterCodes.filter(function (code) {
      const sc = shelterByCode[code];
      if (!sc) return false;
      return !sc.deactivated;
    });
  };
  // Step 2 targets: never-invited users of an ACTIVE service matching the filters.
  const targets = neverInvited.filter(function (p) {
    const active = activeCodesOf(p);
    if (active.length === 0) return false;
    if (stateFilter) {
      const inState = active.some(function (code) {
        return shelterByCode[code]?.state === stateFilter;
      });
      if (!inState) return false;
    }
    if (categoryFilter) {
      const inCat = active.some(function (code) {
        return categoryOfCode(code) === categoryFilter;
      });
      if (!inCat) return false;
    }
    return true;
  });

  // Keep the per-user selection defaulting to ALL of the current filtered set.
  // Keyed on the target id signature so it only resets when the set actually
  // changes (filters/data), never on every render — leaving individual
  // de-selections intact while the filters are stable.
  const targetIdSig = targets
    .map(function (t) {
      return t.id;
    })
    .join(",");
  // Reset selection to ALL only when the target set changes (targetIdSig), not on
  // every render — so individual de-selections survive while filters are stable.
  React.useEffect(
    function () {
      setSelectedUserIds(
        targets.map(function (t) {
          return t.id;
        })
      );
    },
    [targetIdSig]
  );
  const chosen = targets.filter(function (t) {
    return selectedUserIds.indexOf(t.id) !== -1;
  });
  const allUsersSelected = targets.length > 0 && chosen.length === targets.length;
  function toggleUser(id) {
    setSelectedUserIds(function (cur) {
      return cur.indexOf(id) === -1 ? cur.concat(id) : cur.filter((x) => x !== id);
    });
  }
  function toggleAllUsers() {
    setSelectedUserIds(
      allUsersSelected
        ? []
        : targets.map(function (t) {
            return t.id;
          })
    );
  }

  // Sort a DISPLAY-ONLY copy of the targets. Clicking a header toggles asc/desc; a
  // different header sorts ascending first. `targets` (and targetIdSig) are left
  // untouched, so sorting never resets the selection.
  function toggleSort(col) {
    if (sortCol === col) {
      setSortDir(function (d) {
        return d === "asc" ? "desc" : "asc";
      });
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  }
  function sortValue(p, col) {
    if (col === "email") return (p.email || "").toLowerCase();
    if (col === "service") return (p.shelterCodes.join(", ") || "").toLowerCase();
    return (p.name || "").toLowerCase();
  }
  const sortedTargets = targets.slice().sort(function (a, b) {
    const av = sortValue(a, sortCol);
    const bv = sortValue(b, sortCol);
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });
  // Pagination over the sorted set (shared per-device page size, services/pageSize).
  const pageCount = Math.max(1, Math.ceil(sortedTargets.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageTargets = sortedTargets.slice(safePage * pageSize, (safePage + 1) * pageSize);
  // Back to page 1 whenever the sort or the filtered set changes.
  React.useEffect(
    function () {
      setPage(0);
    },
    [sortCol, sortDir, targetIdSig]
  );

  // Clickable column header for the Step-2 list (Name / Email / DFV Service(s)).
  function SortHeader({ col, label }) {
    const active = sortCol === col;
    const arrow = active ? (sortDir === "asc" ? " ▲" : " ▼") : " ⬍";
    return (
      <button
        type="button"
        onClick={function () {
          toggleSort(col);
        }}
        className="text-left uppercase tracking-[0.5px] font-bold text-[11px] text-white bg-transparent border-0 p-0 cursor-pointer select-none"
        style={{ fontFamily: "inherit" }}
      >
        {label}
        {arrow}
      </button>
    );
  }

  // Step 1: the parked (deactivated) DFV Services. Imported services often have no
  // `state`, so match the state filter by state OR the code's token (ZephyrWA###).
  const inactiveServices = shelters.filter(function (s) {
    if (!s.deactivated) return false;
    if (!stateFilter) return true;
    return s.state === stateFilter || (s.shelter_code || "").indexOf(stateFilter) !== -1;
  });
  const allSelected = inactiveServices.length > 0 && selectedIds.length === inactiveServices.length;

  function toggleSelect(id) {
    setSelectedIds(function (cur) {
      return cur.indexOf(id) === -1 ? cur.concat(id) : cur.filter((x) => x !== id);
    });
  }
  function toggleSelectAll() {
    setSelectedIds(allSelected ? [] : inactiveServices.map((s) => s.id));
  }
  async function activateSelected() {
    if (selectedIds.length === 0) return;
    const ok = await confirmDialog({
      title: "Reactivate DFV Services?",
      message: `Reactivate ${selectedIds.length} DFV Service${
        selectedIds.length === 1 ? "" : "s"
      }? Their caseworkers then appear in the invitation list below (step 2).`,
      confirmLabel: `Reactivate ${selectedIds.length}`,
    });
    if (!ok) return;
    setActivating(true);
    const chosenServices = inactiveServices.filter((s) => selectedIds.indexOf(s.id) !== -1);
    let done = 0;
    for (const s of chosenServices) {
      const up = await updateShelterById(s.id, { deactivated: false });
      if (!up.error) {
        logAudit("shelter_reactivated", {
          shelter_code: s.shelter_code,
          name: s.shelter_name,
          via: "invitations",
        });
        done += 1;
      }
    }
    logAudit("dfv_services_activated", { count: done, by: user?.email || "" });
    setActivating(false);
    setSelectedIds([]);
    load();
  }

  const states = Array.from(
    new Set(
      shelters
        .map(function (s) {
          return s.state;
        })
        .filter(Boolean)
    )
  ).sort();

  async function sendInvites() {
    if (chosen.length === 0) return;
    const ok = await confirmDialog({
      title: "Send invitations?",
      message: `Send the onboarding invitation email to ${chosen.length} caseworker${
        chosen.length === 1 ? "" : "s"
      }? Each person gets a login link and is marked as invited.`,
      confirmLabel: `Send ${chosen.length}`,
    });
    if (!ok) return;
    setSending(true);
    setFailures([]);
    setProgress({ done: 0, total: chosen.length });
    const sessionRes = await getSupabaseClient().auth.getSession();
    const token = sessionRes.data?.session ? sessionRes.data.session.access_token : "";
    const fails = [];
    let sent = 0;
    let done = 0;
    for (const p of chosen) {
      const email = p.email;
      try {
        if (!email) throw new Error("no contact email on this person's membership");
        const primaryCode = p.shelterCodes[0] || "";
        const resp = await fetch(EDGE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            action: "send_invite",
            email: email,
            name: p.name || email,
            shelter_name: shelterByCode[primaryCode]?.shelter_name || "",
          }),
        });
        const res = await resp.json();
        if (res.error) throw new Error(res.error);
        // Stamp AFTER the send succeeds — the utility is resumable at any point.
        const up = await updateProfileById(p.id, { invited_at: new Date().toISOString() });
        if (up.error) throw new Error(`sent, but stamping failed: ${up.error.message}`);
        sent += 1;
      } catch (e) {
        logger.warn("Invite failed:", email, e.message);
        fails.push({ email: email || p.name || p.id, message: e.message });
      }
      done += 1;
      setProgress({ done: done, total: chosen.length });
      // Gentle pacing for large batches.
      await new Promise(function (r) {
        setTimeout(r, 250);
      });
    }
    logAudit("invites_sent", {
      sent: sent,
      failed: fails.length,
      state_filter: stateFilter || "all",
      category_filter: categoryFilter || "all",
      attachment: activeAttachmentId ? attachmentDoc?.name || activeAttachmentId : "none",
      by: user?.email || "",
    });
    setFailures(fails);
    setLastRun({ sent: sent, failed: fails.length });
    setSending(false);
    load();
  }

  return (
    <div className="p-6 max-w-[1100px] mx-auto">
      <h1 className="m-0 mb-1 text-[22px] text-zephyr-text">Invitations</h1>
      <p className="m-0 mb-6 text-[13px] text-zephyr-muted">
        Send the onboarding invitation (login link) to caseworkers who have never been invited —
        e.g. accounts created silently by the Unleashed import. Sending marks each person as
        invited, so this screen can be re-run safely in batches.
      </p>

      {loading ? (
        <div className="text-center text-zephyr-muted p-10">Loading...</div>
      ) : (
        <div>
          <Card style={{ padding: 16, marginBottom: 16 }}>
            <div className="flex gap-3 items-center flex-wrap">
              <select
                aria-label="Filter by state"
                value={stateFilter}
                onChange={function (e) {
                  setStateFilter(e.target.value);
                }}
                className="py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] bg-white"
                style={{ fontFamily: "inherit" }}
              >
                <option value="">All states</option>
                {states.map(function (s) {
                  return <option key={s}>{s}</option>;
                })}
              </select>
              <select
                aria-label="Filter by DFV Service category"
                value={categoryFilter}
                onChange={function (e) {
                  setCategoryFilter(e.target.value);
                }}
                className="py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] bg-white max-w-[320px]"
                style={{ fontFamily: "inherit" }}
              >
                <option value="">All DFV Service Categories</option>
                {categories.map(function (c) {
                  return (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  );
                })}
              </select>
              <label className="flex items-center gap-2 text-[13px] text-zephyr-text cursor-pointer">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={function (e) {
                    setShowInactive(e.target.checked);
                    setSelectedIds([]);
                  }}
                />
                Show deactivated DFV Services
              </label>
              <span className="text-[13px]">
                <strong style={{ color: "#B8860B" }}>{chosen.length}</strong> selected
                <span className="text-zephyr-muted">
                  {" "}
                  · {targets.length} awaiting · {alreadyInvited} already invited
                </span>
              </span>
              <div className="flex-1" />
              <Btn onClick={sendInvites} disabled={sending || chosen.length === 0}>
                {sending
                  ? `Sending… ${progress.done}/${progress.total}`
                  : `Send ${chosen.length} invitation${chosen.length === 1 ? "" : "s"}`}
              </Btn>
            </div>
            <div className="text-[12px] mt-2">
              {activeAttachmentId ? (
                <span style={{ color: "#2E7D32" }}>
                  📎 Attaching: <strong>{attachmentDoc?.name || "selected PDF"}</strong> to every
                  invitation{" "}
                  <span className="text-zephyr-muted">(managed in Automatic Emails)</span>
                </span>
              ) : templateAttachment.document_id && !templateAttachment.enabled ? (
                <span className="text-zephyr-muted">
                  Attachment turned off in Automatic Emails ({attachmentDoc?.name || "a PDF"})
                </span>
              ) : (
                <span className="text-zephyr-muted">
                  No attachment set — configure one in Automatic Emails
                </span>
              )}
            </div>
            {sending && (
              <div
                className="h-2 rounded bg-zephyr-off-white overflow-hidden mt-3"
                style={{ maxWidth: 420 }}
              >
                <div
                  className="h-2 bg-zephyr-teal"
                  style={{
                    width: `${
                      progress.total ? Math.round((progress.done / progress.total) * 100) : 0
                    }%`,
                  }}
                />
              </div>
            )}
            {lastRun && !sending && (
              <div className="text-[13px] mt-3">
                <span style={{ color: "#2E7D32", fontWeight: 700 }}>
                  {lastRun.sent} sent {GLYPH.check}
                </span>
                {lastRun.failed > 0 && (
                  <span className="ml-3 text-zephyr-red font-semibold">
                    {lastRun.failed} failed — they remain in the list; fix and re-send.
                  </span>
                )}
              </div>
            )}
            {failures.length > 0 && !sending && (
              <div
                className="border border-zephyr-border rounded-[8px] overflow-y-auto mt-2 text-[12px]"
                style={{ maxHeight: 140 }}
              >
                {failures.map(function (f) {
                  return (
                    <div key={f.email} className="py-[5px] px-3">
                      <b>{f.email}</b>: {f.message}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {showInactive && (
            <Card style={{ padding: 16, marginBottom: 16 }}>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h2 className="m-0 text-[15px] text-zephyr-teal-dark font-bold">
                  Step 1 — Reactivate DFV Services
                </h2>
                <span className="text-[12px] text-zephyr-muted">
                  {inactiveServices.length} deactivated{stateFilter ? ` in ${stateFilter}` : ""}
                </span>
                <div className="flex-1" />
                <Btn onClick={activateSelected} disabled={activating || selectedIds.length === 0}>
                  {activating ? "Reactivating…" : `Reactivate ${selectedIds.length} selected`}
                </Btn>
              </div>
              {inactiveServices.length === 0 ? (
                <div className="text-[13px] text-zephyr-muted">
                  No deactivated DFV Services{stateFilter ? " for this filter" : ""}.
                </div>
              ) : (
                <div className="border border-zephyr-border rounded-[8px] overflow-hidden">
                  <div
                    className="grid gap-0 bg-zephyr-off-white py-[8px] px-[14px] text-[11px] font-bold uppercase tracking-[0.5px] text-zephyr-muted"
                    style={{ gridTemplateColumns: "36px 140px 1fr 70px" }}
                  >
                    <div>
                      <input
                        type="checkbox"
                        aria-label="Select all deactivated DFV Services"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                      />
                    </div>
                    <div>Code</div>
                    <div>Name</div>
                    <div>State</div>
                  </div>
                  <div style={{ maxHeight: 320, overflowY: "auto" }}>
                    {inactiveServices.map(function (s, idx) {
                      const checked = selectedIds.indexOf(s.id) !== -1;
                      return (
                        <label
                          key={s.id}
                          className="grid gap-0 py-[7px] px-[14px] items-center text-[13px] cursor-pointer"
                          style={{
                            gridTemplateColumns: "36px 140px 1fr 70px",
                            borderTop: idx > 0 ? "1px solid #EEF3F2" : "none",
                            background: checked ? "#F0F7F5" : idx % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
                          }}
                        >
                          <div>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={function () {
                                toggleSelect(s.id);
                              }}
                            />
                          </div>
                          <div className="font-semibold text-[#00695C]">{s.shelter_code}</div>
                          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                            {s.shelter_name}
                          </div>
                          <div className="text-zephyr-muted">{s.state || "—"}</div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="text-[12px] text-zephyr-muted mt-2">
                Reactivating moves a service live; its never-invited caseworkers then appear below
                for Step 2.
              </div>
            </Card>
          )}

          {targets.length === 0 ? (
            <Card style={{ padding: 24 }}>
              <div className="text-[13px] text-zephyr-muted">
                No one matching the current filters is awaiting an invitation.
              </div>
            </Card>
          ) : (
            <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
              <div
                className="grid gap-0 bg-zephyr-teal-dark text-white py-[10px] px-[18px] text-[11px] font-bold uppercase tracking-[0.5px] items-center"
                style={{ gridTemplateColumns: "40px 1.2fr 1.4fr 1fr" }}
              >
                <div>
                  <input
                    type="checkbox"
                    aria-label="Select all caseworkers"
                    checked={allUsersSelected}
                    onChange={toggleAllUsers}
                  />
                </div>
                <SortHeader col="name" label="Name" />
                <SortHeader col="email" label="Email" />
                <SortHeader col="service" label="DFV Service(s)" />
              </div>
              {pageTargets.map(function (p, idx) {
                const checked = selectedUserIds.indexOf(p.id) !== -1;
                const rowIdx = safePage * pageSize + idx;
                return (
                  <label
                    key={p.id}
                    className="grid gap-0 py-[9px] px-[18px] items-center text-[13px] cursor-pointer"
                    style={{
                      gridTemplateColumns: "40px 1.2fr 1.4fr 1fr",
                      borderTop: idx > 0 ? "1px solid #EEF3F2" : "none",
                      background: checked ? "#F0F7F5" : rowIdx % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
                    }}
                  >
                    <div>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={function () {
                          toggleUser(p.id);
                        }}
                      />
                    </div>
                    <div className="font-semibold">{p.name}</div>
                    <div
                      className={`overflow-hidden text-ellipsis whitespace-nowrap ${
                        p.email ? "text-zephyr-muted" : "text-zephyr-red"
                      }`}
                    >
                      {p.email || "(no email — can't invite)"}
                    </div>
                    <div className="text-zephyr-muted">{p.shelterCodes.join(", ") || "—"}</div>
                  </label>
                );
              })}
              {pageCount > 1 && (
                <div className="px-[18px] pb-3 pt-1">
                  <PageNav page={safePage} pageCount={pageCount} onPage={setPage} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
